import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import WhatsappFloat from "@/components/site/WhatsappFloat";
import QuoteDialog from "@/components/site/QuoteDialog";
import SEO from "@/components/seo/SEO";
import ProductCatalogCard, { ProductCatalogGrid } from "@/components/site/ProductCatalogCard";
import ProductSearch from "@/components/site/ProductSearch";
import { PRODUCT_IMAGES } from "@/data/products";

/**
 * Normalize string for accent-insensitive search: lowercased, without diacritics.
 */
const normalize = (str = "") =>
  str
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const CATALOG_ITEMS = [
  {
    key: "ecobag",
    name: "Sacola de Algodão",
    code: "MN-ECO-01",
    image: PRODUCT_IMAGES.ecobag,
    productName: "Sacola de Algodão Personalizada",
    features: ["Algodão cru 160g/m²", "Silk Screen, Transfer Digital, DTF", "Pedido mínimo: 50 un.", "Prazo: 7 a 12 dias"],
    link: "/produtos/ecobag-algodao",
  },
  {
    key: "rm",
    name: "Sacola de Algodão",
    code: "MN-ECO-RM",
    image: "/legacy/sc-com-uma-empress-o-digital.png",
    productName: "Sacola de Algodão Personalizada",
    features: ["Transfer digital 20×30 cm", "Algodão cru 160g/m²", "Pedido mínimo: 50 un."],
    link: "/produtos/ecobag-algodao",
  },
  {
    key: "sustain",
    name: "Sacola de Algodão",
    code: "MN-ECO-SW",
    image: "/legacy/bolsa-ecol-gica-para-a-sustentabilidade-2026.png",
    productName: "Sacola de Algodão Personalizada",
    features: ["Transfer digital 28×30 cm", "Algodão cru 160g/m²", "Pedido mínimo: 50 un."],
    link: "/produtos/ecobag-algodao",
  },
  {
    key: "braz",
    name: "Sacola de Algodão",
    code: "MN-ECO-BT",
    image: "/legacy/chatgpt-image-26_06_2026-07_43_56.png",
    productName: "Sacola de Algodão Personalizada",
    features: ["Transfer digital 28×40 cm", "Algodão cru 160g/m²", "Pedido mínimo: 50 un."],
    link: "/produtos/ecobag-algodao",
  },
  {
    key: "eurochem",
    name: "Sacola de Algodão",
    code: "MN-ECO-EC",
    image: "/legacy/whatsapp-image-2026-06-24-at-17.07.46-2-.jpeg",
    productName: "Sacola de Algodão Personalizada",
    features: ["Transfer digital 38×40 cm", "Algodão cru 160g/m²", "Pedido mínimo: 50 un."],
    link: "/produtos/ecobag-algodao",
  },
  {
    key: "picpay",
    name: "Sacola de Algodão",
    code: "MN-ECO-PP",
    image: "/legacy/whatsapp-image-2026-06-24-at-17.07.40-1-.jpeg",
    productName: "Sacola de Algodão Personalizada",
    features: ["Transfer digital 38×40 cm", "Algodão cru 160g/m²", "Pedido mínimo: 50 un."],
    link: "/produtos/ecobag-algodao",
  },
  {
    key: "tnt",
    name: "Sacola TNT",
    code: "MN-TNT-01",
    image: PRODUCT_IMAGES.tntNovoCapa,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 80g/m² ou 100g/m²", "Diversas cores", "Silk Screen, Transfer Digital, DTF", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "tnt-sesc",
    name: "Sacola TNT",
    code: "MN-TNT-SC",
    image: PRODUCT_IMAGES.tntNovo2,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 100g/m² preto", "Transfer Digital branco", "Aplicação real", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "tnt-aaprev",
    name: "Sacola TNT",
    code: "MN-TNT-AP",
    image: PRODUCT_IMAGES.tntNovo3,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 100g/m² branco", "Transfer Digital colorido", "Alças azul marinho", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "tnt-pdj",
    name: "Sacola TNT",
    code: "MN-TNT-PD",
    image: PRODUCT_IMAGES.tntNovo4,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 100g/m² preto", "Transfer Digital colorido", "Alças pretas", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "tnt-branco-alca",
    name: "Sacola TNT",
    code: "MN-TNT-BA",
    image: PRODUCT_IMAGES.tntBrancoAlca,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 80g/m² premium", "Alça em fita colorida", "Transfer digital full-color", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "tnt-branco-transfer",
    name: "Sacola TNT",
    code: "MN-TNT-BT",
    image: PRODUCT_IMAGES.tntBrancoTransfer,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 80g/m²", "Transfer digital fotográfico", "Ideal para logos coloridos", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "tnt-verde",
    name: "Sacola TNT",
    code: "MN-TNT-VD",
    image: PRODUCT_IMAGES.tntVerde,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 80g/m² — verde", "Formato 33×40 cm", "Silk Screen 1 cor", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "tnt-rosa",
    name: "Sacola TNT",
    code: "MN-TNT-RS",
    image: PRODUCT_IMAGES.tntRosa,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 80g/m² — rosa", "Formato 33×40 cm", "Ideal para campanhas femininas", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "tnt-branca-alca-alt",
    name: "Sacola TNT",
    code: "MN-TNT-BP",
    image: PRODUCT_IMAGES.tntBrancoAlcaAlt,
    productName: "Sacola TNT Personalizada",
    features: ["TNT 100g/m² reforçado", "Alça premium colorida", "Transfer digital", "Pedido mínimo: 100 un."],
    link: "/produtos/sacola-tnt",
  },
  {
    key: "ecobag-catalogo",
    name: "Sacola de Algodão",
    code: "MN-ECO-CT",
    image: PRODUCT_IMAGES.ecobagCatalogo,
    productName: "Sacola de Algodão Personalizada",
    features: ["Algodão cru 160g/m²", "Impressão frente e verso", "Alças reforçadas", "Pedido mínimo: 50 un."],
    link: "/produtos/ecobag-algodao",
  },
  {
    key: "ecobag-04",
    name: "Sacola de Algodão",
    code: "MN-ECO-04",
    image: PRODUCT_IMAGES.ecobagAlt,
    productName: "Sacola de Algodão Personalizada",
    features: ["Algodão cru 200g/m²", "Acabamento reforçado", "Silk Screen, Transfer Digital ou DTF", "Pedido mínimo: 50 un."],
    link: "/produtos/ecobag-algodao",
  },
  {
    key: "squeeze",
    name: "Squeeze de Alumínio",
    code: "MN-SQZ-01",
    image: PRODUCT_IMAGES.squeeze,
    productName: "Squeeze de Alumínio Personalizado",
    features: ["Alumínio leve e resistente", "500 ml / 750 ml", "Gravação a laser ou silk"],
    link: "/produtos/squeeze",
  },
  {
    key: "toalha",
    name: "Toalha de Mão",
    code: "MN-TLH-01",
    image: PRODUCT_IMAGES.toalha,
    productName: "Toalha de Mão Personalizada",
    features: ["Algodão 380 a 450 g/m²", "Bordado ou estampa", "30×50 / 40×70 cm"],
    link: "/produtos/toalha-personalizada",
  },
  {
    key: "toalha-social-slim",
    name: "Toalha Social Slim 23×36",
    code: "MN-TLH-SS",
    image: PRODUCT_IMAGES.socialSlimCapaCorreta,
    productName: "Toalha Social Slim Lisa 23×36 cm",
    features: ["Algodão fio penteado", "Formato slim 23×36 cm", "Silk Screen, Transfer Digital, DTF", "Pedido mínimo: 50 un."],
    link: "/produtos/toalha-social-slim",
  },
  {
    key: "toalha-fitness-slim",
    name: "Toalha Fitness Slim 29×70",
    code: "MN-TLH-FS",
    image: PRODUCT_IMAGES.fitnessSlimCapaCorreta,
    productName: "Toalha Fitness Slim Lisa 29×70 cm",
    features: ["Algodão felpudo alta absorção", "Formato slim 29×70 cm", "Silk Screen, Transfer Digital, DTF", "Pedido mínimo: 50 un."],
    link: "/produtos/toalha-fitness-slim",
  },
  {
    key: "caderno-moleskine",
    name: "Caderno Moleskine",
    code: "MN-CAD-MK",
    image: PRODUCT_IMAGES.cadernoCapa,
    productName: "Caderno Moleskine Personalizado",
    features: ["Capa dura colorida", "Elástico e marcador de página", "Miolo pautado 80 fls", "Pedido mínimo: 50 un."],
    link: "/produtos/caderno-moleskine",
  },
  {
    key: "chapeu-panama",
    name: "Chapéu Estilo Panamá",
    code: "MN-CHP-PN",
    image: PRODUCT_IMAGES.chapeuBege,
    productName: "Chapéu Estilo Panamá Personalizado",
    features: ["Acabamento que remete à palha natural", "Fita grosgrain preta", "Bordado ou etiqueta personalizada", "Pedido mínimo: 50 un."],
    link: "/produtos/chapeu-panama",
  },
  {
    key: "camiseta-algodao-gola-careca",
    name: "Camiseta de Algodão Gola Careca",
    code: "MN-CAM-GC",
    image: PRODUCT_IMAGES.camisetaCapa,
    productName: "Camiseta de Algodão Gola Careca Personalizada",
    features: ["Algodão penteado 30.1", "Gola careca reforçada", "Silk Screen, Transfer Digital, DTF", "Pedido mínimo: 50 un."],
    link: "/produtos/camiseta-algodao-gola-careca",
  },
  {
    key: "mochila-corporativa",
    name: "Mochila Corporativa",
    code: "MN-MCH-EX",
    image: PRODUCT_IMAGES.mochilaCorporativa,
    productName: "Mochila Corporativa Personalizada",
    features: ["Poliéster premium preto", "Compartimento notebook 15,6\"", "Alças ergonômicas", "Pedido mínimo: 50 un."],
    link: "/produtos/mochila-corporativa",
  },
  {
    key: "sacola-mochila-ecologica",
    name: "Sacola Tipo Mochila Ecológica",
    code: "MN-MCH-EC",
    image: PRODUCT_IMAGES.mochilaEcoAlgodao,
    productName: "Sacola Tipo Mochila Ecológica Personalizada",
    features: ["Algodão cru bege claro", "Alças em cordão mochila", "Opção microfibra impermeável", "Pedido mínimo: 50 un."],
    link: "/produtos/sacola-mochila-ecologica",
  },
  {
    key: "saquinho-algodao-cru",
    name: "Saquinho em Algodão Cru",
    code: "MN-SAQ-01",
    image: PRODUCT_IMAGES.saquinhoAlgodao,
    productName: "Saquinho em Algodão Cru Personalizado",
    features: ["Algodão cru 100% natural", "Fechamento em cordão duplo", "Silk Screen, Transfer Digital, DTF", "Pedido mínimo: 50 un."],
    link: "/produtos/saquinho-algodao-cru",
  },
  {
    key: "caneca-personalizada",
    name: "Caneca Personalizada",
    code: "MN-CAN-01",
    image: PRODUCT_IMAGES.canecaPersonalizada,
    productName: "Caneca Personalizada Cerâmica Premium",
    features: ["Cerâmica esmaltada branca", "Capacidade 325 ml", "Transfer Digital de alta definição", "Pedido mínimo: 50 un."],
    link: "/produtos/caneca-personalizada",
  },
  {
    key: "dtf-textil",
    name: "DTF Têxtil Personalizado",
    code: "MN-DTF-01",
    image: PRODUCT_IMAGES.dtfTextil,
    productName: "DTF Têxtil Personalizado — Serviço",
    features: ["Impressão têxtil DTF", "Full color CMYK + branco", "Aplicação em diversos tecidos", "Alta durabilidade"],
    link: "/produtos/dtf-textil",
  },
  {
    key: "transfer-digital",
    name: "Transfer Digital Personalizado",
    code: "MN-TRD-01",
    image: PRODUCT_IMAGES.transferDigital,
    productName: "Transfer Digital Personalizado — Serviço",
    features: ["Transfer digital termocolante", "Cores vibrantes CMYK", "Aplicação em tecidos e brindes", "Acabamento profissional"],
    link: "/produtos/transfer-digital",
    category: "Personalização Têxtil",
    keywords: "transfer digital termocolante cmyk personalização brinde tecido",
  },
  {
    key: "polo-masculina-preta",
    name: "Camiseta Polo Masculina",
    code: "MN-POL-PT",
    image: PRODUCT_IMAGES.poloPreta,
    productName: "Camiseta Polo Masculina Personalizada",
    features: ["Piquet ou malha PV premium", "Silk Screen, DTF, Bordado ou Transfer", "Diversas cores e tamanhos", "Pedido mínimo: 50 un."],
    link: "/produtos/camiseta-polo-masculina",
    category: "Vestuário Personalizado",
    keywords: "camiseta polo masculina personalizada uniforme empresarial polo bordada polo dtf polo silk screen preta evento academia igreja",
  },
  {
    key: "polo-masculina-branca",
    name: "Camiseta Polo Masculina",
    code: "MN-POL-BR",
    image: PRODUCT_IMAGES.poloBranca,
    productName: "Camiseta Polo Masculina Personalizada",
    features: ["Piquet branco premium", "Silk Screen, DTF, Bordado", "Uniforme corporativo", "Pedido mínimo: 50 un."],
    link: "/produtos/camiseta-polo-masculina",
    category: "Vestuário Personalizado",
    keywords: "camiseta polo masculina branca uniforme empresa polo bordada corporativa academia igreja",
  },
  {
    key: "polo-masculina-azul-marinho",
    name: "Camiseta Polo Masculina",
    code: "MN-POL-AZ",
    image: PRODUCT_IMAGES.poloAzulMarinho,
    productName: "Camiseta Polo Masculina Personalizada",
    features: ["Piquet azul marinho premium", "Silk Screen, DTF, Bordado", "Ideal para uniformes", "Pedido mínimo: 50 un."],
    link: "/produtos/camiseta-polo-masculina",
    category: "Vestuário Personalizado",
    keywords: "camiseta polo masculina azul marinho uniforme empresarial polo bordada evento academia igreja",
  },
  {
    key: "porta-copo-mnaief",
    name: "Porta Copo em Couro Sintético",
    code: "MN-PCP-MN",
    image: PRODUCT_IMAGES.portaCopoMnaief,
    productName: "Porta Copo em Couro Sintético Ecológico Personalizado",
    features: ["Couro sintético ecológico premium", "Gravação a laser ou hot-stamping", "Fácil limpeza", "Pedido mínimo: 100 un."],
    link: "/produtos/porta-copo-couro-sintetico",
    category: "Brindes Personalizados",
    keywords: "porta copo personalizado couro sintetico ecologico brinde corporativo restaurante hotel bar evento cafeteria promocional",
  },
  {
    key: "porta-copo-nexway",
    name: "Porta Copo em Couro Sintético",
    code: "MN-PCP-NX",
    image: PRODUCT_IMAGES.portaCopoNexway,
    productName: "Porta Copo em Couro Sintético Ecológico Personalizado",
    features: ["Couro sintético ecológico marrom", "Gravação institucional a laser", "Alta resistência", "Pedido mínimo: 100 un."],
    link: "/produtos/porta-copo-couro-sintetico",
    category: "Brindes Personalizados",
    keywords: "porta copo personalizado couro sintetico ecologico brinde corporativo restaurante hotel bar evento cafeteria",
  },
];

/**
 * Build a single searchable string per catalog item. Includes name, code,
 * product long name, category, keywords and features so the search can match
 * any of these.
 */
const buildSearchIndex = (item) =>
  normalize(
    [
      item.name,
      item.code,
      item.productName,
      item.category,
      item.keywords,
      ...(item.features || []),
    ]
      .filter(Boolean)
      .join(" ")
  );

const CATALOG_INDEX = CATALOG_ITEMS.map((it) => ({
  item: it,
  search: buildSearchIndex(it),
}));

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  name: "Catálogo de Produtos M NAIEF",
  itemListElement: CATALOG_ITEMS.map((it, i) => ({
    "@type": "Offer",
    position: i + 1,
    itemOffered: { "@type": "Product", name: it.productName, image: it.image, sku: it.code },
  })),
};

export default function Catalog() {
  const [open, setOpen] = useState(false);
  const [defaultProduct, setDefaultProduct] = useState(undefined);
  const [query, setQuery] = useState("");

  const handleQuote = (productName) => {
    setDefaultProduct(productName);
    setOpen(true);
  };

  const filteredItems = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return CATALOG_ITEMS;
    // Split by whitespace so multi-word queries match items containing all tokens
    const tokens = q.split(/\s+/);
    return CATALOG_INDEX.filter(({ search }) =>
      tokens.every((tok) => search.includes(tok))
    ).map(({ item }) => item);
  }, [query]);

  return (
    <div className="App" data-testid="catalog-page">
      <SEO
        title="Catálogo — Brindes Corporativos Personalizados"
        description="Catálogo profissional de brindes corporativos personalizados: sacolas de algodão, sacolas TNT, squeezes, toalhas, camiseta polo masculina e porta copo em couro sintético. Aplicações reais para empresas em todo o Brasil."
        keywords="catálogo brindes corporativos, sacola de algodão personalizada, sacola TNT, squeeze, toalha personalizada, camiseta polo personalizada, polo masculina personalizada, uniforme empresarial, porta copo personalizado, porta copo em couro sintético, brindes personalizados, produtos personalizados"
        url="/catalogo"
        jsonLd={jsonLd}
      />
      <Header onQuoteClick={() => setOpen(true)} />

      <main className="pt-[120px] lg:pt-[180px] pb-20 bg-white">
        <div className="mn-container">
          <nav
            className="text-xs uppercase tracking-[0.2em] text-[var(--mn-dark)] flex items-center gap-2 mb-7"
            aria-label="breadcrumb"
            data-testid="catalog-breadcrumb"
          >
            <Link to="/" className="hover:text-[var(--mn-gold)]">Home</Link>
            <ChevronRight size={12} />
            <span className="text-[var(--mn-gold)]">Catálogo</span>
          </nav>

          <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-12">
            <div>
              <span className="mn-eyebrow">Catálogo comercial</span>
              <h1 className="mn-h-display text-[2.5rem] sm:text-[3rem] lg:text-[3.5rem] mt-4 leading-[1.05]">
                Linha completa de brindes <br className="hidden md:block" />
                <span style={{ color: "var(--mn-gold)" }}>personalizados M NAIEF.</span>
              </h1>
            </div>
            <div className="max-w-md">
              <p className="text-[var(--mn-dark)] leading-relaxed text-sm sm:text-base">
                Catálogo profissional por linha de produto. Cada item exibe fotografia, especificações e botão direto para orçamento personalizado.
              </p>
            </div>
          </header>

          {/* Search bar — sits above the product grid */}
          <div className="mb-10 lg:mb-14">
            <ProductSearch
              value={query}
              onChange={setQuery}
              onClear={() => setQuery("")}
              resultCount={query ? filteredItems.length : undefined}
            />
          </div>

          {filteredItems.length > 0 ? (
            <ProductCatalogGrid testId="catalog-grid">
              {filteredItems.map((it) => (
                <ProductCatalogCard
                  key={it.key}
                  name={it.name}
                  code={it.code}
                  image={it.image}
                  features={it.features}
                  link={it.link}
                  onQuote={() => handleQuote(it.productName)}
                  testIdSlug={it.key}
                />
              ))}
            </ProductCatalogGrid>
          ) : (
            <div
              className="border border-dashed border-[var(--mn-line)] rounded-2xl py-20 px-6 text-center bg-[#FAFAFA]"
              data-testid="catalog-empty"
            >
              <p className="font-serif-display font-bold text-xl sm:text-2xl text-[var(--mn-black)]">
                Nenhum produto encontrado.
              </p>
              <p className="mt-3 text-sm text-[var(--mn-dark)]">
                Tente outro termo ou{" "}
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="underline underline-offset-4 text-[var(--mn-gold)] hover:text-[var(--mn-gold-hover)]"
                  data-testid="catalog-empty-reset"
                >
                  limpe a pesquisa
                </button>
                .
              </p>
            </div>
          )}

          <div className="mt-14 grid sm:grid-cols-3 gap-6 text-xs text-[var(--mn-dark)]" data-testid="catalog-info">
            <div>
              <div className="text-[var(--mn-gold)] uppercase tracking-[0.22em] font-semibold mb-2">Pedido mínimo</div>
              A partir de 50 unidades por modelo.
            </div>
            <div>
              <div className="text-[var(--mn-gold)] uppercase tracking-[0.22em] font-semibold mb-2">Personalização</div>
              Silk-screen, DTF, transfer digital, bordado e gravação a laser.
            </div>
            <div>
              <div className="text-[var(--mn-gold)] uppercase tracking-[0.22em] font-semibold mb-2">Entrega</div>
              Todo o Brasil, prazos a partir de 5 dias úteis.
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsappFloat />
      <QuoteDialog open={open} onOpenChange={setOpen} defaultProduct={defaultProduct} />
    </div>
  );
}
